// Where a new block is introduced.
//
// The level generator stamps `isFeature` / `unlocks` INSIDE the level config,
// and every hop in between — the collection, the release snapshot, the export
// files, the player endpoint — carries that config verbatim. So the fields
// arrive here exactly as authored, and equally unvalidated: everything below
// treats them as untrusted input.
//
// Deliberately a leaf module: it imports nothing. level_config.ts imports both
// of this file's consumers at runtime (test_level_bridge and
// level_pack_service), so anything those two pull in at runtime has to sit
// outside that cycle — see the client's module-cycle rule.

/** The block labels that unlock exactly at this level, e.g. ["скло"]. */
export function readUnlocks( raw: unknown ): string[]
{
  if ( !Array.isArray( raw ) )
    return [];

  const labels: string[] = [];
  for ( const entry of raw )
  {
    // Only real labels — String(null) would happily become "null".
    if ( typeof entry !== "string" && !( typeof entry === "number" && isFinite( entry ) ) )
      continue;

    const label = String( entry ).trim();
    if ( label.length > 0 )
      labels.push( label );
  }
  return labels;
}

/**
 * Reads the feature marker off a raw level config.
 *
 * A level that unlocks something counts as a feature level even with the flag
 * missing: the flag is the pacing beat the curve draws, the list is the hard
 * evidence. Mirrors featureInfo() in the admin panel's curve-studio.js, so both
 * ends agree on what a feature level is.
 *
 * Every level authored before this existed has neither field and reads as an
 * ordinary level rather than an error.
 */
export function readFeature( raw: Record<string, unknown> | null | undefined ): { isFeature: boolean; unlocks: string[] }
{
  if ( !raw || typeof raw !== "object" )
    return { isFeature: false, unlocks: [] };

  const unlocks = readUnlocks( raw.unlocks );
  return { isFeature: raw.isFeature === true || unlocks.length > 0, unlocks };
}
